import { chatStore$ } from "@/lib/ContactsMap"
import { mergeContactData } from "@/features/PowerDialer/CentralPanel/utils/contactUtils"
import type { EnrichedDialerData } from "@/features/PowerDialer/CentralPanel/ListTable"
import type { TDialerList } from "@/schemas/user.schema"

export const DEFAULT_COLUMN_ORDER = ["name", "title", "business", "phone", "tz", "nTouches", "lastTouch", "connectRate", "lastNote"]

// Helper function to compute cached dialer list values
export function computeDialerListValues(dialerListData: TDialerList | null | undefined) {
  const list = dialerListData?.list ?? []
  const lastCall = dialerListData?.lastCall ?? null
  // Legacy: sync no longer provides authoritative pastCalls for table rendering,
  // but we keep reading this field for backwards compatibility in the store cache.
  const pastCalls = dialerListData?.pastCalls ?? []
  const contacts = chatStore$.contacts.get() ?? {}

  // Combine lastCall + list (server guarantees no duplicates)
  const lastCallArray = lastCall && Object.keys(lastCall).length > 0 ? [lastCall] : []
  const allItems = [...lastCallArray, ...list]

  const dialerContactIds = allItems.map((item: any) => (item?.id ? String(item.id) : null))

  // Compute dialer list items
  const dialerListItems = dialerContactIds
    .map((contactId, idx) => {
      const originalItem = allItems[idx]
      const merged = mergeContactData(originalItem, contactId ? contacts[contactId] : null, contactId)
      // Generate a unique key using _id + index to handle duplicate contacts in sequences
      // The index is stable within the list and _id provides semantic meaning
      return merged ? { ...merged, _rowKey: `${merged._id || "row"}-${idx}` } : null
    })
    .filter(Boolean) as EnrichedDialerData[]

  // Compute past call contact IDs (server guarantees no duplicates)
  const pastCallsArray = Array.isArray(pastCalls) ? pastCalls : []
  const pastCallContactIds = pastCallsArray.map((item: any) => (item?.id ? String(item.id) : null))

  // Compute past call list items
  const pastCallListItems = pastCallContactIds
    .map((contactId, idx) => {
      const originalItem = pastCallsArray[idx]
      const merged = mergeContactData(originalItem, contactId ? contacts[contactId] : null, contactId)
      // Generate a unique key using _id + index to handle duplicate contacts in sequences
      // The index is stable within the list and _id provides semantic meaning
      return merged ? { ...merged, _rowKey: `${merged._id || "row"}-${idx}` } : null
    })
    .filter(Boolean) as EnrichedDialerData[]

  return {
    cachedDialerContactIds: dialerContactIds,
    cachedDialerListItems: dialerListItems,
    cachedPastCallContactIds: pastCallContactIds,
    cachedPastCallListItems: pastCallListItems,
  }
}

export function getStorageKey(listId: string | null): string {
  const numericId = listId ? parseInt(listId) || 0 : 0
  return `power-dialer-columns-${numericId}`
}

export function getDefaultVisibleColumns(availableColumns: string[]): string[] {
  // Filter to only include columns that exist in available columns
  return DEFAULT_COLUMN_ORDER.filter((col) => availableColumns.includes(col))
}

export function getDefaultColumnOrder(availableColumns: string[]): { id: string; order: number }[] {
  // First, add columns from DEFAULT_COLUMN_ORDER that exist in availableColumns
  const orderedColumns: { id: string; order: number }[] = []
  let orderIndex = 0

  for (const col of DEFAULT_COLUMN_ORDER) {
    if (availableColumns.includes(col)) {
      orderedColumns.push({ id: col, order: orderIndex++ })
    }
  }

  // Then add remaining columns not in DEFAULT_COLUMN_ORDER
  for (const col of availableColumns) {
    if (!DEFAULT_COLUMN_ORDER.includes(col)) {
      orderedColumns.push({ id: col, order: orderIndex++ })
    }
  }

  return orderedColumns
}

interface StoredColumnConfig {
  enabledIds?: string[]
  columnOrder?: { id: string; order: number }[]
}

export function loadColumnConfiguration(listId: string | null, availableColumns: string[]): { enabledIds: string[]; columnOrder: { id: string; order: number }[] } {
  if (typeof window === "undefined") {
    const defaultVisible = getDefaultVisibleColumns(availableColumns)
    return {
      enabledIds: defaultVisible,
      columnOrder: getDefaultColumnOrder(availableColumns),
    }
  }

  try {
    const storageKey = getStorageKey(listId)
    const stored = localStorage.getItem(storageKey)

    if (stored) {
      try {
        const config = JSON.parse(stored) as StoredColumnConfig
        if (config.columnOrder && Array.isArray(config.columnOrder)) {
          const validEnabledIds = (config.enabledIds || []).filter((id) => availableColumns.includes(id))
          const validOrder = config.columnOrder.filter((item) => availableColumns.includes(item.id))
          return { enabledIds: validEnabledIds, columnOrder: validOrder }
        }
      } catch {}

      const enabledColumnIds = JSON.parse(stored) as string[]
      const validEnabledIds = enabledColumnIds.filter((id) => availableColumns.includes(id))
      return {
        enabledIds: validEnabledIds,
        columnOrder: getDefaultColumnOrder(availableColumns),
      }
    }
  } catch (error) {
    console.warn("Failed to load column configuration:", error)
  }

  const defaultVisible = getDefaultVisibleColumns(availableColumns)
  return {
    enabledIds: defaultVisible,
    columnOrder: getDefaultColumnOrder(availableColumns),
  }
}

export function saveColumnConfiguration(listId: string | null, enabledIds: string[], columnOrder?: { id: string; order: number }[]): void {
  if (typeof window === "undefined") return

  try {
    const storageKey = getStorageKey(listId)
    const config: StoredColumnConfig = {
      enabledIds,
      columnOrder: columnOrder || enabledIds.map((id, idx) => ({ id, order: idx })),
    }
    localStorage.setItem(storageKey, JSON.stringify(config))
  } catch (error) {
    console.warn("Failed to save column configuration:", error)
  }
}
